import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Save, FileText, AlertTriangle, X } from 'lucide-react';
import GeneralTab from './tabs/GeneralTab';
import ContentTab from './tabs/ContentTab';

const ContractEdit = ({ contractId, onBack, onSaved, isNew = false }) => {
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('general');
    const [hasChanges, setHasChanges] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);

    // Initialize new contract
    useEffect(() => {
        if (isNew) {
            setContract({
                title: '',
                type: 0,
                content: '',
                cancellation_policy_obligation: 0
            });
            setLoading(false);
        }
    }, [isNew]);

    // Load contract data
    const loadContract = useCallback(async () => {
        if (isNew) return;

        try {
            setLoading(true);
            const response = await fetch(`/index.php/contracts/getContractByID?id=${contractId}`);
            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                setContract(data);
                setError(null);
            }
        } catch (err) {
            console.error('Error loading contract:', err);
            setError('Fehler beim Laden des Vertrags');
        } finally {
            setLoading(false);
        }
    }, [contractId, isNew]);

    useEffect(() => {
        if (contractId && !isNew) {
            loadContract();
        }
    }, [contractId, isNew, loadContract]);

    // Update contract data
    const updateContract = (updates) => {
        setContract(prev => ({
            ...prev,
            ...updates
        }));
        setHasChanges(true);

        // Clear validation errors for updated fields
        if (updates.title && validationErrors.title) {
            setValidationErrors(prev => ({ ...prev, title: null }));
        }
        if (updates.type && validationErrors.type) {
            setValidationErrors(prev => ({ ...prev, type: null }));
        }
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!contract.title || contract.title.trim() === '') {
            errors.title = 'Titel ist erforderlich';
        }

        if (!contract.type || contract.type === 0) {
            errors.type = 'Kategorie ist erforderlich';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Save contract
    const handleSave = async () => {
        if (!validateForm()) {
            setActiveTab('general');
            return;
        }

        setSaving(true);
        try {
            const endpoint = isNew
                ? '/index.php/contracts/createContractJSON'
                : '/index.php/contracts/updateContractJSON';

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(contract)
            });

            const result = await response.json();

            if (!result.success) {
                setError(result.error || 'Fehler beim Speichern');
                setSaving(false);
                return;
            }

            setHasChanges(false);
            if (onSaved) {
                onSaved();
            }
            onBack();
        } catch (err) {
            console.error('Error saving contract:', err);
            setError('Fehler beim Speichern des Vertrags');
        } finally {
            setSaving(false);
        }
    };

    // Handle back with unsaved changes warning
    const handleBack = () => {
        if (hasChanges) {
            setShowUnsavedModal(true);
        } else {
            onBack();
        }
    };

    // Confirm discard changes
    const handleConfirmDiscard = () => {
        setShowUnsavedModal(false);
        onBack();
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Lädt...</span>
                </div>
            </div>
        );
    }

    if (error && !contract) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
                <button className="btn btn-link" onClick={onBack}>
                    Zurück zur Liste
                </button>
            </div>
        );
    }

    if (!contract) {
        return null;
    }

    const tabs = [
        { id: 'general', label: 'Allgemeines' },
        { id: 'content', label: 'Vertragsinhalt' }
    ];

    // Styles
    const actionBarStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid var(--border-light, #e2e8f0)',
        background: 'var(--bg-secondary, #f5f7f9)',
        borderRadius: '12px 12px 0 0',
        marginBottom: '0'
    };

    const backButtonStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        border: '1px solid var(--border-medium, #cbd5e1)',
        borderRadius: '8px',
        background: 'var(--bg-card, #ffffff)',
        color: 'var(--text-primary, #2c3e50)',
        fontSize: '0.875rem',
        fontWeight: '500',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    const saveButtonStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1.25rem',
        border: 'none',
        borderRadius: '8px',
        background: 'var(--accent-slate, #5b7c8d)',
        color: 'white',
        fontSize: '0.875rem',
        fontWeight: '600',
        cursor: saving ? 'not-allowed' : 'pointer',
        opacity: saving ? 0.7 : 1,
        transition: 'all 0.2s ease'
    };

    const tabButtonStyle = (isActive) => ({
        border: 'none',
        borderBottom: isActive ? '2px solid var(--accent-slate, #5b7c8d)' : '2px solid transparent',
        color: isActive ? 'var(--accent-slate, #5b7c8d)' : 'var(--text-secondary, #64748b)',
        fontWeight: isActive ? '600' : '400',
        background: 'transparent',
        padding: '0.75rem 1rem',
        marginBottom: '-1px',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    });

    const typeLabel = contract.type === 1 ? 'Jobs' : contract.type === 2 ? 'Hochzeiten' : '';

    return (
        <div className="contract-edit-view">
            {/* Action Bar */}
            <div style={actionBarStyle}>
                <button
                    style={backButtonStyle}
                    onClick={handleBack}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-hover, #f1f5f9)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-card, #ffffff)';
                    }}
                >
                    <ArrowLeft size={18} />
                    Zurück
                </button>
                <button
                    style={saveButtonStyle}
                    onClick={handleSave}
                    disabled={saving}
                    onMouseEnter={(e) => {
                        if (!saving) e.currentTarget.style.background = 'var(--accent-mint, #7fb3a1)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--accent-slate, #5b7c8d)';
                    }}
                >
                    {saving ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                            Speichern...
                        </>
                    ) : (
                        <>
                            <Save size={18} />
                            Speichern
                        </>
                    )}
                </button>
            </div>

            {/* Header */}
            <div
                style={{
                    padding: '1.5rem',
                    borderBottom: '1px solid var(--border-light, #e2e8f0)',
                    background: 'var(--bg-card, #ffffff)'
                }}
            >
                <div className="d-flex align-items-center gap-3">
                    <FileText size={28} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                    <div>
                        <h4 className="mb-0 fw-semibold" style={{ color: 'var(--text-primary, #2c3e50)' }}>
                            {isNew ? 'Neuer Vertrag' : (contract.title || 'Vertrag bearbeiten')}
                        </h4>
                        <small className="text-muted">
                            {isNew ? 'Erstellen Sie eine neue Vertragsvorlage' : `Vorlage für ${typeLabel}`}
                        </small>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="alert alert-danger alert-dismissible fade show m-3" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
            )}

            {/* Tabs */}
            <div
                style={{
                    background: 'var(--bg-card, #ffffff)',
                    borderRadius: '0 0 12px 12px',
                    border: '1px solid var(--border-light, #e2e8f0)',
                    borderTop: 'none'
                }}
            >
                <div style={{ borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>
                    <ul className="nav nav-tabs" style={{ borderBottom: 'none', padding: '0 1rem' }}>
                        {tabs.map(tab => (
                            <li className="nav-item" key={tab.id}>
                                <button
                                    style={tabButtonStyle(activeTab === tab.id)}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    {activeTab === 'general' && (
                        <GeneralTab
                            contract={contract}
                            onChange={updateContract}
                            validationErrors={validationErrors}
                        />
                    )}
                    {activeTab === 'content' && (
                        <ContentTab
                            contract={contract}
                            onChange={updateContract}
                        />
                    )}
                </div>
            </div>

            {/* Unsaved Changes Modal */}
            {showUnsavedModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000
                    }}
                    onClick={() => setShowUnsavedModal(false)}
                >
                    <div
                        style={{
                            background: 'var(--bg-card, #ffffff)',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '420px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '1rem 1.25rem',
                                borderBottom: '1px solid var(--border-light, #e2e8f0)'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '8px',
                                        background: 'rgba(245, 158, 11, 0.1)'
                                    }}
                                >
                                    <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
                                </div>
                                <h5
                                    style={{
                                        margin: 0,
                                        fontSize: '1.0625rem',
                                        fontWeight: '600',
                                        color: 'var(--text-primary, #2c3e50)'
                                    }}
                                >
                                    Ungespeicherte Änderungen
                                </h5>
                            </div>
                            <button
                                onClick={() => setShowUnsavedModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.25rem',
                                    color: 'var(--text-secondary, #64748b)',
                                    borderRadius: '4px'
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '1.25rem' }}>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: '0.9375rem',
                                    color: 'var(--text-secondary, #64748b)',
                                    lineHeight: '1.5'
                                }}
                            >
                                Sie haben ungespeicherte Änderungen. Wenn Sie fortfahren, gehen diese verloren.
                            </p>
                        </div>

                        {/* Modal Footer */}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '0.75rem',
                                padding: '1rem 1.25rem',
                                borderTop: '1px solid var(--border-light, #e2e8f0)',
                                background: 'var(--bg-secondary, #f5f7f9)'
                            }}
                        >
                            <button
                                onClick={() => setShowUnsavedModal(false)}
                                style={{
                                    padding: '0.5rem 1rem',
                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                    borderRadius: '8px',
                                    background: 'var(--bg-card, #ffffff)',
                                    color: 'var(--text-primary, #2c3e50)',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={handleConfirmDiscard}
                                style={{
                                    padding: '0.5rem 1rem',
                                    border: 'none',
                                    borderRadius: '8px',
                                    background: '#ef4444',
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                Änderungen verwerfen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContractEdit;
